import { GenerateQRCodeParams, GenerateQRCodeResponse } from '@/types/qrCode';
import httpClient from './http-client';
import { UserTasksResponse, NewUserTasksResponse } from '@/types/notifications';
import { NON_AUTH_HEADER } from '@/constants';

export default class DeviceService {
    private baseApiEndpoint = '/api/dvcs'
    private videoBaseUrl: string | undefined;
    private accessToken: string | undefined;

    constructor(idToken: string | undefined = undefined, accessToken: string | undefined = undefined) {
        this.baseApiEndpoint = import.meta.env.VITE_BYPASS_PROXY === 'true'
            ? '' : this.baseApiEndpoint;
        this.videoBaseUrl = import.meta.env.VITE_VIDEO_BASE_URL;
        this.accessToken = accessToken;
    }

    async getList(params: any): Promise<any> {
        let response, filterParams, pageParams, size;
        filterParams = JSON.stringify(params?.filter) || '';
        pageParams = params?.page || 1;
        size = params?.size || 100;

        const urlParams = new URLSearchParams({
            filter: filterParams,
            page: pageParams,
            size: size
        });
        // Only add currentGuid and excludeLinked if explicitly specified
        if (typeof params?.currentGuid !== 'undefined') {
            urlParams.append('currentGuid', params.currentGuid);
        }
        if (typeof params?.excludeLinked !== 'undefined') {
            urlParams.append('excludeLinked', params.excludeLinked);
        }

        // Add sorting parameters if provided
        if (params?.sort_by && params?.sort_order) {
            urlParams.append('sort_by', params.sort_by);
            urlParams.append('sort_order', params.sort_order);
        }
        urlParams.append('requestId', new Date().getTime().toString());

        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices?${urlParams.toString()}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {

            // throw error;
        }

        return response?.data || null;
    }

    async getTypes(): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/type`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {

            // throw error;
        }

        return response?.data || null;
    }

    async getSearchResults(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/search?serial_number=${params?.searchText}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            // throw error;
        }

        return response?.data || null;
    }

    async getChartData(params?: any): Promise<any> {
        let response;
        // requestType = device_type, lifecycle_status
        try {
            let url = `${this.baseApiEndpoint}/devices/chart/${params?.requestType}/?gdm_customer_number=${params?.gdmNumber}`;

            // Add search parameter if provided
            if (params?.search) {
                url += `&search=${encodeURIComponent(params.search)}`;
            }

            // Add lifecycle_status parameter if provided
            if (params?.lifecycle_status) {
                url += `&lifecycle_status=${encodeURIComponent(params.lifecycle_status)}`;
            }

            // Add device_type parameter if provided and not empty
            if (params?.device_type && params.device_type !== '' &&
                (!Array.isArray(params.device_type) || params.device_type.length > 0)) {
                url += `&device_type=${encodeURIComponent(params.device_type)}`;
            }

            // Add device_state parameter if provided and not empty
            if (params?.device_state && params.device_state !== '') {
                url += `&device_state=${encodeURIComponent(params.device_state)}`;
            }

            // Add location_id parameter if provided
            if (params?.location_id !== undefined && params?.location_id !== null) {
                url += `&location_id=${encodeURIComponent(params.location_id)}`;
            }

            // Add device_category parameter if provided
            if (params?.device_category && params.device_category !== '') {
                url += `&device_category=${encodeURIComponent(params.device_category)}`;
            }

            response = await httpClient.get(url, {
                headers: {
                    'Content-Type': 'application/json'
                },
            });
        } catch (error) {
            // throw error;
        }

        return response?.data || null;
    }

    async getDetails(params?: any): Promise<any> {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        // When no auth token, pass the static non-auth header for view-only access
        if (!this.accessToken) {
            Object.assign(headers, NON_AUTH_HEADER);
        }

        const response = await httpClient.get(
            `${this.baseApiEndpoint}/devices/${params?.deviceid}?requestId=${new Date().getTime().toString()}`,
            { headers }
        );
        return response?.data || null;
    }

  async getImagesAndVideos(params?: any): Promise<any> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
    };

    // When no auth token, pass the static non-auth header for view-only access
    if (!this.accessToken) {
        Object.assign(headers, NON_AUTH_HEADER);
    }

    let response;
    try {
        response = await httpClient.get(
            `${this.baseApiEndpoint}/devices/${params?.deviceid}/images-and-videos`,
            { headers }
        );
    } catch (error) {
        console.error('Error fetching images and videos:', error);
    }
    let data = response?.data;
    data?.videos?.forEach((video: any) => {
      video.url = video.thumbnail;
      console.log("Video object:", video);
      console.log("video.id:", video.id);
      if (video.id && this.videoBaseUrl) {
        video.original = this.videoBaseUrl.replace("%22", "") + video.id;
      }
    });
    return data;
  }

    async getComponents(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/${params?.deviceid}/components`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching details:', error);
            // throw error;
        }

        return response?.data || null;
    }

    async getAttributes(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/${params?.deviceid}/attributes`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching details:', error);
            // throw error;
        }

        return response?.data || null;
    }

    async getPartsAndAccessories(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/${params?.deviceid}/parts-and-accessories?user_id=${params?.userid}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching details:', error);
            // throw error;
        }

        return response?.data || null;
    }

    async getPartsImages(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/spare-parts-image?stock_keeping_unit=${params?.stock_keeping_unit}&store=${params?.store}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching details:', error);
            // throw error;
        }

        return response?.data || null;
    }


    async getTrainings(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/${params?.deviceid}/trainings`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching details:', error);
            // throw error;
        }

        return response?.data || null;
    }

    async generateEspirReport(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.post(
                `${this.baseApiEndpoint}/devices/generate-espir-report`,
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            // throw error;
        }

        return response?.data || null;
    }

    async getRecommendedReplacements(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/recommended-replacements?base_model=${encodeURIComponent(params?.baseModel)}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error fetching recommended replacements:', error);
            // throw error;
        }

        return response?.data || null;
    }
    // Documents and drawings methods
    async getDocumentsAndDrawings(deviceId: string, category: string): Promise<any> {
        try {
            const response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/${deviceId}/documents-and-drawings?category=${category}`,
                {
                    headers: {
                        'Content-Type': 'application/json'
                    },
                }
            );
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    // Unified download method for documents using direct URL
    async downloadDocumentDirect(url: string): Promise<Blob> {
        try {
            const response = await httpClient.get(url, {
                responseType: 'blob',
                headers: {
                    'Content-Type': 'application/json'
                },
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async unlinkDeviceComponent(payload: any): Promise<any> {
        let response;
        try {
            response = await httpClient.post(
                `${this.baseApiEndpoint}/devices`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error unlinking device component:', error);
            throw error;
        }

        return response?.data || null;
    }

    async linkDevice(payload: any): Promise<any> {
        let response;
        try {
            response = await httpClient.post(
                `${this.baseApiEndpoint}/devices`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        // 'X-Authorization': `Bearer ${this.accessToken}`,
                    },
                }
            );
        } catch (error) {
            console.error('Error linking device:', error);
            throw error;
        }

        return response?.data || null;
    }

    async createDevice(params?: any): Promise<any> {
        let response
        try {
            response = await httpClient.post(
                `${this.baseApiEndpoint}/devices`,
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
        } catch (error) {
            console.error('Error creating device:', error);
            // throw error;
        }

        return response?.data || null;
    }

    async moveDevice(params?: any): Promise<any> {
        let response;
        try {
            response = await httpClient.post(
                `${this.baseApiEndpoint}/devices/move`,
                params,
                {
                    headers: {
                        "Content-Type": "application/json"
                    },
                }
            );
        } catch (error) {
            console.error("Error moving device:", error);
            throw error;
        }

        return response?.data || null;
    }

    // QR Code methods
    async generateQRCodes(params: GenerateQRCodeParams): Promise<{ task_id?: string } | null> {
        try {
            const response = await httpClient.post(
                `${this.baseApiEndpoint}/devices/qr-code-template`,
                params,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Generate QR Codes Error:', error);
            return null;
        }
    }

    async getQRCodesByTaskId(task_id: string): Promise<GenerateQRCodeResponse | null> {
        try {
            const response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/qr-code-request/${task_id}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Get QR Codes by Task ID Error:', error);
            return null;
        }
    }
        // Notification/Task methods
    async getUserTasks(params: { page: number; userId: string }): Promise<UserTasksResponse> {
        try {
            const response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/tasks?requestId=${new Date().getTime()}&page=${params.page}&user_id=${params.userId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching user tasks:', error);
            throw error;
        }
    }

    async getUnreadNotifications(params: { page?: number; userId: string; isRead?: boolean }): Promise<NewUserTasksResponse> {
        try {
            const queryParams = new URLSearchParams({
                page: String(params.page || 1),
                user_id: params.userId,
            });

            if (params.isRead !== undefined) {
                queryParams.append('is_read', String(params.isRead));
            }

            const response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/tasks?${queryParams.toString()}&requestId=${new Date().getTime()}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching unread notifications:', error);
            throw error;
        }
    }

    async markNotificationsAsRead(notificationIds: string[], userId: string): Promise<any> {
        try {
            const response = await httpClient.post(
                `${this.baseApiEndpoint}/devices/tasks/read`,
                {
                    ids: notificationIds,
                    user_id: userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error marking notifications as read:', error);
            throw error;
        }
    }

    async getBlobFile(linkUrl: string): Promise<any> {
        try {
            const response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/generate-blob-files/?${linkUrl}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error getting blob file:', error);
            throw error;
        }
    }

    async exportTaskCsv(notificationId: string): Promise<Blob> {
        try {
            const response = await httpClient.get(
                `${this.baseApiEndpoint}/devices/tasks/export?notification_id=${notificationId}&requestId=${new Date().getTime()}`,
                {
                    responseType: 'blob',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error exporting task CSV:', error);
            throw error;
        }
    }

    async getQrCodes(deviceId: string): Promise<any> {
        try {
            const response = await httpClient.post(
                `${this.baseApiEndpoint}/devices/qr-code-template`,
                { device_ids: [deviceId] },
                {
                    headers: {
                        'Content-Type': 'application/json',
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error getting QR codes:', error);
            throw error;
        }
    }

    async requestService(
    deviceId: string,
    userId: string,
    serviceData: { request_type: string; notes: string }
  ): Promise<any> {
    let response;
    try {
      const params = new URLSearchParams({
        user_id: userId,
      });

      response = await httpClient.post(
        `${
          this.baseApiEndpoint
        }/devices/${deviceId}/request-service?${params.toString()}`,
        serviceData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    } catch (error) {
      console.error("Error requesting service:", error);
      throw error;
    }

    return response?.data || null;
  }
}
